/**
 * Add a service to a client: pick the channel, pick the provider, enter what
 * that provider needs.
 *
 * Save also verifies when the provider can, because "saved" says nothing about
 * whether the provider will accept the credentials and the admin is about to
 * find out anyway. A refusal still saves the service — the credentials were
 * entered on purpose — and the card then says what the provider said.
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowLeft, BookOpen, Check } from 'lucide-react';
import {
  ApiError,
  Alert,
  Badge,
  Button,
  Can,
  Input,
  Modal,
  Switch,
  api,
  cn,
  toast,
} from '@connect/web-shared';

import {
  ProviderFields,
  fieldsValid,
  initialValues,
  requiredFilled,
  type FieldValues,
} from './provider-fields';
import { docPath } from '../docs/docs-page';
import { ChannelIcon } from './channel-icon';
import { messagingKeys, type ChannelDef, type ClientChannel } from './types';

type Step = 'channel' | 'provider' | 'details';

export function AddServiceDialog({
  clientPublicId,
  clientName,
  catalog,
  existing,
  open,
  onClose,
  onAdded,
}: {
  clientPublicId: string;
  clientName: string;
  catalog: ChannelDef[];
  existing: ClientChannel[];
  open: boolean;
  onClose: () => void;
  onAdded: (channel: ClientChannel) => void;
}) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>('channel');
  const [channelId, setChannelId] = useState<string | null>(null);
  const [providerId, setProviderId] = useState<string | null>(null);
  const [label, setLabel] = useState('');
  const [credentials, setCredentials] = useState<FieldValues>({});
  const [settings, setSettings] = useState<FieldValues>({});
  const [primary, setPrimary] = useState(false);

  const channel = catalog.find((c) => c.id === channelId) ?? null;
  const provider = channel?.providers.find((p) => p.id === providerId) ?? null;
  const connected = useMemo(
    () => new Set(existing.map((c) => `${c.channel}:${c.provider}`)),
    [existing],
  );
  const channelHasService = existing.some((c) => c.channel === channelId);

  useEffect(() => {
    if (!open) return;
    // One channel is no choice at all, so it is not offered as one.
    const only = catalog.length === 1 ? catalog[0] : undefined;
    setChannelId(only?.id ?? null);
    setStep(only ? 'provider' : 'channel');
    setProviderId(null);
    setLabel('');
  }, [open, catalog]);

  const chooseProvider = (id: string) => {
    const next = channel?.providers.find((p) => p.id === id);
    if (!next) return;
    setProviderId(id);
    setCredentials(initialValues(next.credentials));
    setSettings(initialValues(next.settings));
    setPrimary(false);
    setStep('details');
  };

  const save = useMutation({
    mutationFn: async () => {
      if (!channel || !provider) throw new Error('Choose a provider first.');
      const created = await api.post<ClientChannel>(
        `/messaging/clients/${clientPublicId}/channels`,
        {
          channel: channel.id,
          provider: provider.id,
          label: label.trim() || null,
          credentials,
          settings,
          // Left to the server for a channel's first service, which is its primary.
          ...(channelHasService ? { is_primary: primary } : {}),
        },
      );
      if (!provider.capabilities.includes('verify')) return { channel: created, checked: false };
      try {
        const verified = await api.post<ClientChannel>(
          `/messaging/channels/${created.public_id}/verify`,
        );
        return { channel: verified, checked: true };
      } catch {
        // Saved, and the provider could not be reached to check. Not a failure
        // of the save, which is what the admin did.
        return { channel: created, checked: false };
      }
    },
    onSuccess: ({ channel: saved, checked }) => {
      void queryClient.invalidateQueries({ queryKey: messagingKeys.channels(clientPublicId) });
      if (saved.last_error) {
        toast.error('Saved, but the provider refused the credentials', saved.last_error);
      } else if (checked) {
        toast.success(`${saved.provider_label} connected`, 'The provider accepted the credentials.');
      } else {
        toast.success(`${saved.provider_label} added`, 'Verify it from the service to check the credentials.');
      }
      onAdded(saved);
      onClose();
    },
  });

  const fieldErrors = save.error instanceof ApiError ? save.error.fieldErrors : undefined;
  const ready =
    provider !== null &&
    requiredFilled(provider.credentials, credentials) &&
    requiredFilled(provider.settings, settings) &&
    fieldsValid(provider.credentials, credentials) &&
    fieldsValid(provider.settings, settings) &&
    // A second service on a channel is named, as the server requires.
    (!channelHasService || label.trim().length > 0);

  const back =
    step === 'details'
      ? () => setStep('provider')
      : step === 'provider' && catalog.length > 1
        ? () => setStep('channel')
        : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      dismissOnBackdrop={false}
      title={
        step === 'details' && provider
          ? `Connect ${provider.label}`
          : step === 'provider' && channel
            ? `Add ${channel.label}`
            : 'Add a service'
      }
      description={
        step === 'details'
          ? `The credentials of ${clientName}'s own account with this provider.`
          : step === 'provider'
            ? 'Choose the provider that delivers it.'
            : `What should ${clientName} be able to send?`
      }
      footer={
        <>
          {back ? (
            <Button variant="ghost" size="sm" onClick={back} disabled={save.isPending} className="mr-auto">
              <ArrowLeft className="size-3.5" />
              Back
            </Button>
          ) : null}
          <Button variant="secondary" size="sm" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          {step === 'details' ? (
            <Button size="sm" loading={save.isPending} disabled={!ready} onClick={() => save.mutate()}>
              {provider?.capabilities.includes('verify') ? 'Save and verify' : 'Save'}
            </Button>
          ) : null}
        </>
      }
    >
      {step === 'channel' ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {catalog.map((option) => (
            <ChoiceCard
              key={option.id}
              title={option.label}
              description={option.description}
              icon={<ChannelIcon channel={option.id} />}
              meta={`${option.providers.length} provider${option.providers.length === 1 ? '' : 's'}`}
              onClick={() => {
                setChannelId(option.id);
                setStep('provider');
              }}
            />
          ))}
        </div>
      ) : null}

      {step === 'provider' && channel ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {channel.providers.map((option) => {
            // A provider that names its accounts takes another account; any
            // other is connected once.
            const already = connected.has(`${channel.id}:${option.id}`);
            const another = already && Boolean(option.account_field);
            return (
              <ChoiceCard
                key={option.id}
                title={option.label}
                description={option.description}
                icon={<ChannelIcon channel={channel.id} />}
                disabled={already && !another}
                meta={another ? 'Connect another account' : already ? 'Already connected' : undefined}
                onClick={() => chooseProvider(option.id)}
              />
            );
          })}
        </div>
      ) : null}

      {step === 'details' && channel && provider ? (
        <div className="space-y-5">
          {save.error ? (
            <Alert tone="danger" title="Could not save">
              {save.error instanceof ApiError ? save.error.message : 'Could not reach the server.'}
            </Alert>
          ) : null}

          {provider.guide ? (
            <Can permission="core.doc.view">
              <p className="flex items-center gap-1.5 text-xs text-muted">
                <BookOpen className="size-3.5 text-accent" />
                Where to find each of these:{' '}
                <Link to={docPath(provider.guide)} target="_blank" className="text-accent hover:underline">
                  the {provider.label} setup guide
                </Link>
                .
              </p>
            </Can>
          ) : null}

          <section className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-faint">Credentials</h3>
            <ProviderFields
              specs={provider.credentials}
              values={credentials}
              onChange={setCredentials}
              errors={fieldErrors}
              errorPrefix="credentials"
            />
          </section>

          {provider.settings.length ? (
            <section className="space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-faint">Settings</h3>
              <ProviderFields
                specs={provider.settings}
                values={settings}
                onChange={setSettings}
                errors={fieldErrors}
                errorPrefix="settings"
              />
            </section>
          ) : null}

          <section className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Label"
              value={label}
              maxLength={120}
              placeholder={provider.label}
              required={channelHasService}
              hint={
                channelHasService
                  ? 'Required: the client already has a service on this channel, and this name tells them apart.'
                  : 'Optional. Tells two services on one channel apart.'
              }
              onChange={(event) => setLabel(event.target.value)}
            />
            {channelHasService ? (
              <Switch
                label={`Primary ${channel.label} service`}
                checked={primary}
                hint={`Sends use the primary unless told otherwise. ${clientName} already has one.`}
                onChange={(event) => setPrimary(event.target.checked)}
              />
            ) : (
              <p className="flex items-center gap-1.5 self-center text-xs text-muted">
                <Badge tone="accent">
                  <Check className="size-2.5" />
                  Primary
                </Badge>
                {clientName}'s first {channel.label} service is its primary.
              </p>
            )}
          </section>
        </div>
      ) : null}
    </Modal>
  );
}

function ChoiceCard({
  title,
  description,
  icon,
  meta,
  disabled,
  onClick,
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
  meta?: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex w-full items-start gap-3 rounded-card border border-line bg-surface p-3.5 text-left transition-colors',
        'hover:border-accent hover:bg-accent-subtle focus-visible:border-accent focus-visible:outline-none',
        'disabled:pointer-events-none disabled:opacity-55',
      )}
    >
      <span className="flex size-9 shrink-0 items-center justify-center rounded-control bg-surface-2 text-accent">
        {icon}
      </span>
      <span className="min-w-0 space-y-0.5">
        <span className="block text-sm font-medium text-heading">{title}</span>
        <span className="block text-xs text-muted">{description}</span>
        {meta ? <span className="block text-2xs text-faint">{meta}</span> : null}
      </span>
    </button>
  );
}
